import { Routes } from '@angular/router';
import { ActivityEntityName, TenantStatus, PaymentType, SecurityModuleName, FeatureFlags, MetadataEntityRecordScope } from '@jms/model';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { AccessDeniedComponent, PageNotFoundComponent } from '@jms/ui/ui.error.pages';
import { entityInspectorRoute, provideEntityInspectors } from '@workbuddy/ui/v2/blocks/entity.inspector';
import { entityListRoute, entityNoAccessRoute, entityRecordRoute } from '@workbuddy/ui/v2/blocks/entity.record';
import { entityDialogRoute, provideEntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { ShellComponent, AuthoriseComponent, InactiveUserComponent, DeletedUserComponent, PortalSelectComponent } from './components';
import { ConsentComponent } from './components/consent';
import { legacyEntityRouteGuard } from './legacy/legacy.entity.route.guard';
import { InactiveStatusUserComponent } from './components/inactive-status-user/inactive.status.user.component';
import { MaintenanceTenantComponent } from './components/maintenance-tenant/maintenance.tenant.component';
import { InactiveTenantComponent } from './components/inactive-tenant/inactive.tenant.component';
import { AuthGuard } from './guards';
import { HomeGuard } from './guards/home.guard';
import { agentRouteGuard } from '@workbuddy/ai/agent';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';
import { nativeJobGanttCanActivate, nativeJobGanttCanMatch } from '@workbuddy/modules/boards/job/job.gantt.native/job.gantt.native.guard';

/** Finance-pro probe job (SH022855) — shared parent for items / tasks / files boards. */
const financeProProbeJobId = '45ea3c3d09f20b700933ed4bb12b7259';

/** Finance-pro probe checklist ("Alist 1", 13 templates: 3 targets, phase tags, 4 multi-evidence). */
const financeProProbeChecklistId = '74d1ed8e-f096-478d-9971-4e3985344d1f';

export const app_routes: Routes = [
    {
        path: '',
        component: ShellComponent,
        canActivate: [AuthGuard],
        // On the shell ROUTE, not only on ShellComponent: `canDeactivate` on the inspector's child route
        // resolves against the route injector, where component-level providers are invisible (NG0201).
        // This route lives as long as the shell, so inspectors still share one service instance.
        providers: [provideEntityInspectors(), provideEntityDialogs()],
        children: [
            // Shell-hosted inplace edit inspectors, on the `inspector` outlet. Authorization may resolve
            // edit to preview; batched create/edit uses the dialog outlet below.
            entityInspectorRoute,
            // Generic full-page record route, `/entity/:slug/:operation`. One config for every entity;
            // an entity opts in by declaring a manifest `slug`. This is what an inspector's Open full
            // page hands off to when the entity declares no legacy `routes.record`.
            entityRecordRoute,
            // `/entity/no-access`: where a denied record link lands, instead of a board the same right closes.
            entityNoAccessRoute,
            // Generic list page, `/entity/:slug`, for entities that declare a manifest `menu`. After
            // no-access, which it would otherwise swallow as a slug.
            entityListRoute,
            // Shell-hosted blocking dialogs on the `dialog` outlet, for batched create/edit. Reachable
            // from any board at any depth, unlike a board's own dialog outlet.
            entityDialogRoute,
            {
                path: 'home',
                canActivate: [HomeGuard],
                loadChildren: () => import('../../../../libs/tenant/module.jms.dashboard').then(m => m.JmsDashboardModule),
                data: { title: 'Home', icon: 'fa-home', animation: 'homePage', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'contacts',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.contacts').then(m => m.ContactsModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Contacts', icon: 'fa-address-card', restrictions: [TenantStatus.Ready] }
            // },
            // {
            //     path: 'crm',
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'CRM', icon: 'fa-address-book-o', restrictions: [TenantStatus.Ready] },
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.crm').then(m => m.JmsCrmModule),
            // },
            {
                path: 'crm',
                canActivate: [AuthorizeGuard],
                loadChildren: () => import('@workbuddy/modules/boards/crm').then(m => m.CRM_MODULE_ROUTES),
                data: {
                    title: 'CRM',
                    icon: 'fa-address-book-o',
                    restrictions: [TenantStatus.Ready]
                },
            },
            // {
            //     path: 'crmpro',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.crm.pro').then(m => m.CrmShellModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'CRM Pro', icon: 'fa-address-card', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'compliance',
                loadChildren: () => import('../../../../libs/shared/module.individual.compliance').then(m => m.JmsIndividualComplianceModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Compliance', icon: 'fa-certificate', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'timesheets',
                loadChildren: () => import('../../../../libs/tenant/module.jms.timesheets.pro').then(m => m.ManualTimesheetsModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Timesheets', icon: 'fa-clock-o', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'quotes',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes').then(m => m.QuoteModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
            // },
            // {
            //     path: 'quotes-pro',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Quotes Pro', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
            // },
            // {
            //     path: 'assets',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.asset.register').then(m => m.AssetRegisterModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Assets', icon: 'icomoon icomoon-warehouse', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'assets',
                loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_MODULE_ROUTES),
                canActivate: [AuthorizeGuard],
                data: {
                    title: 'Assets',
                    icon: 'fa-solid fa-warehouse-full',
                    restrictions: [TenantStatus.Ready],
                },
            },
            // {
            //     path: 'assets-1',
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Assets', icon: 'fa-solid fa-warehouse-full', restrictions: [TenantStatus.Ready] },
            //     children: [
            //         // {
            //         //     path: 'assets-old',
            //         //     loadChildren: () => import('../../../../libs/tenant/module.jms.asset.register').then(m => m.AssetRegisterModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Assets - Old', icon: 'icomoon icomoon-warehouse', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'customer-assets',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/assets/assets.routes').then(m => m.ASSETS_ROUTES),
            //             data: { title: 'Customer Assets', icon: 'fa-solid fa-warehouse-full', restrictions: [TenantStatus.Ready] }

            //         },
            //         {
            //             path: 'internal-equipment',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment/equipment.routes').then(m => m.EQUIPMENT_ROUTES),
            //             data: {
            //                 title: 'Internal Equipment',
            //                 icon: 'fa-solid fa-toolbox',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Internal Equipment',
            //                     entity: ActivityEntityName.EquipmentInternal,
            //                 }
            //             }
            //         },
            //         {
            //             path: 'equipment',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_VEHICLE_ROUTES),
            //             data: {
            //                 title: 'Equipment',
            //                 icon: 'fa-solid fa-inventory',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Equipment',
            //                     entity: ActivityEntityName.EquipmentHireable,
            //                 }
            //             }
            //         },
            //         {
            //             path: 'vehicles',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_VEHICLE_ROUTES),
            //             data: {
            //                 title: 'Vehicles',
            //                 icon: 'fa-solid fa-car',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Vehicles',
            //                     entity: ActivityEntityName.EquipmentVehicle,
            //                 }
            //             }
            //         },
            //         // {
            //         //     path: 'serial-numbers',
            //         //     canActivate: [AuthorizeGuard],
            //         //     loadChildren: () => import('@workbuddy/modules/boards/equipment.serialised').then(m => m.EQUIPMENT_SERIALISED_ROUTES),
            //         //     data: { title: 'Serial Numbers', icon: 'fa-solid fa-toolbox', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'stock-locations',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/stock.locations').then(m => m.STOCK_LOCATION_ROUTES),
            //             data: { title: 'Stock Locations', icon: 'fa-solid fa-pallet-boxes', restrictions: [TenantStatus.Ready] }
            //         }
            //     ]
            // },

            {
                path: 'quotes',
                canActivate: [AuthorizeGuard],
                data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] },
                children: [
                    // {
                    //     path: 'quotes-deprecated',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes').then(m => m.QuoteModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Quotes [Deprecated]', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'quotes-new',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('quotes')],
                        data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quote-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quote.templates').then(m => m.QuoteTemplatesModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('quote-templates')],
                        data: { title: 'Quote Templates', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                ]
            },

            {
                path: 'sales',
                canActivate: [AuthorizeGuard],
                data: { title: 'Sales', icon: 'fa-money', restrictions: [TenantStatus.Ready] },
                children: [
                    {
                        path: 'leads',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.leads').then(m => m.JmsLeadsModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Leads', icon: 'fa-users', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'leads-pro',
                        loadChildren: () => import('@workbuddy/modules/boards/leads/leads.routes').then(m => m.LEADS_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Leads Pro', icon: 'fa-users', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'opportunities',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.opportunities').then(m => m.JmsOpportunitiesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Opportunities', icon: 'fa-handshake-o', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'opportunities-pro',
                        loadChildren: () => import('@workbuddy/modules/boards/opportunities/opportunities.routes').then(m => m.OPPORTUNITIES_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Opportunities Pro', icon: 'fa-handshake-o', restrictions: [TenantStatus.Ready] }
                    },

                    {
                        path: 'quotes-new',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('quotes')],
                        data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quote-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quote.templates').then(m => m.QuoteTemplatesModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('quote-templates')],
                        data: { title: 'Quote Templates', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                ]
            },

            {
                path: 'jobs',
                data: { title: 'Operations', icon: 'icomoon icomoon-jobs', restrictions: [TenantStatus.Ready] },
                children: [
                    // {
                    //     path: 'projects',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.projects').then(m => m.JmsProjectsModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Projects', icon: 'fa-tasks', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'operations',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.operations').then(m => m.OperationsModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Jobs', icon: 'fa-tachometer', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'recurring',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.recurring.jobs').then(m => m.RecurringJobsModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Recurring Jobs', icon: 'icomoon icomoon-recurring-jobs', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'recurring',
                        loadChildren: () => import('@workbuddy/modules/boards/recurring.jobs/recurring.jobs.routes').then(m => m.RECURRING_JOBS_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Recurring Jobs', icon: 'icomoon icomoon-recurring-jobs', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'job-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.job.templates').then(m => m.JobTemplatesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Job Templates', icon: 'icomoon icomoon-job-template', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'project-templates',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.project.templates').then(m => m.ProjectTemplatesModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Project Templates', icon: 'icomoon icomoon-job-template', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'resource-location',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.resource.location').then(m => m.ResourceLocationModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Resource Location', icon: 'fa-map-marker', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'homes-nsw-notices',
                        loadChildren: () => import('@workbuddy/modules/boards/homes.nsw.notices/homes.nsw.notices.routes').then(m => m.HOMES_NSW_NOTICES_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Notices', icon: 'fa-flag-swallowtail', restrictions: [TenantStatus.Ready] }
                    },

                    {
                        path: 'bif-events',
                        loadChildren: () => import('@workbuddy/modules/boards/bif.events/bif.events.routes').then(m => m.BIF_EVENTS_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'BIF Events', icon: 'fa-triangle-exclamation', restrictions: [TenantStatus.Ready] }
                    },

                    {
                        path: "gantt/:jobId",
                        loadComponent: () => import('@workbuddy/modules/boards/job/job.gantt.chart').then(m => m.JobGanttChartComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Gantt', icon: 'fa-regular fa-chart-gantt', restrictions: [TenantStatus.Ready], hidden: true }
                    },

                    {
                        path: 'gantt-native/:jobId',
                        loadComponent: () => import('@workbuddy/modules/boards/job/job.gantt.native').then(m => m.JobGanttNativeComponent),
                        canMatch: [nativeJobGanttCanMatch],
                        canActivate: [nativeJobGanttCanActivate],
                        data: { title: 'Gantt — Native Preview', icon: 'fa-regular fa-chart-gantt', restrictions: [TenantStatus.Ready], hidden: true }
                    },

                    {
                        path: 'view',
                        loadChildren: () => import('@workbuddy/ui/modules/manage.job/route-host').then(m => m.JOB_DETAILS_ROUTES),
                        data: { title: 'Manage Job', type: 'Job' }
                    },
                    {
                        path: 'view-pro',
                        loadChildren: () => import('@workbuddy/ui/modules/manage.job.pro/manage.job.pro.routes').then(m => m.MANAGE_JOB_PRO_ROUTES),
                        canMatch: [() => ConfigService.environment.featureFlags?.[FeatureFlags.ManageJobPro] ?? false],
                        data: { title: 'Manage Job Pro', type: 'Job' }
                    }
                ]
            },
            {
                path: 'locations',
                loadComponent: () => import('@workbuddy/ui/modules/locations/locations.dashboard.component').then(m => m.LocationsDashboardComponent),
                canActivate: [AuthorizeGuard],
                data: { title: 'Locations', icon: 'fa-map-marker', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'works',
                loadChildren: () => import('@workbuddy/modules/boards/works').then(m => m.WORK_ROUTES),
                canActivate: [AuthorizeGuard],
                data: { title: 'Job v2', icon: 'fa-cube', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'operations',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.operations').then(m => m.OperationsModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Operations', icon: 'fa-tachometer', restrictions: [TenantStatus.Ready] }
            // },



            // {
            //     path: 'scheduling',
            //     data: { title: 'Scheduling', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] },
            //     children: [
            //         // {
            //         //     path: 'allocations',
            //         //     loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations').then(m => m.JmsJobAllocationsModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Job Allocations', icon: 'fa fa-calendar', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'allocations',
            //             loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations.pro').then(m => m.JmsJobAllocationsProModule),
            //             canActivate: [AuthorizeGuard],
            //             data: { title: 'Job Allocations', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] }
            //         },
            //         // {
            //         //     path: 'appointments',
            //         //     loadChildren: () => import('../../../../libs/module.jms.scheduling.appointment/core').then(m => m.AppointmentCoreModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Appointments', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'runsheets',
            //             loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.runsheet/core').then(m => m.SchedulingCoreModule),
            //             canActivate: [AuthorizeGuard],
            //             data: { title: 'Runsheets', icon: 'fa-truck', restrictions: [TenantStatus.Ready] }
            //         },
            //     ]
            // },

            {
                path: 'scheduler',
                data: { title: 'Scheduler', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] },
                canActivate: [AuthorizeGuard],
                loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations.pro').then(m => m.JmsJobAllocationsProModule),
            },

            {
                path: 'finance',
                data: { title: 'Finance', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] },
                children: [
                    {
                        path: 'pending',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.finance').then(m => m.FinanceModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Pending', icon: 'fa-clock-o', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'invoices',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.invoices').then(m => m.InvoicesModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('invoices')],
                        data: { title: 'Invoices', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'invoices-consolidation',
                        loadComponent: () => import('../../../../workbuddy/modules/boards/bulk.invoices').then(m => m.BulkInvoicesDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Consolidated Invoices', icon: 'fa-solid fa-file-invoice-dollar', restrictions: [TenantStatus.Ready], topLevel: true }
                    },
                    {
                        path: 'bills',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.bills').then(m => m.BillsModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('bills')],
                        data: { title: 'Bills', icon: 'fa-money', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'accounts-receivable',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.payments').then(m => m.PaymentsModule),
                        canLoad: [AuthorizeGuard],
                        canActivate: [legacyEntityRouteGuard('payments')],
                        data: { title: 'Payments', type: PaymentType.Receivable, icon: 'fa-credit-card', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'accounts-payable',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.payments').then(m => m.PaymentsModule),
                    //     canLoad: [AuthorizeGuard],
                    //     data: { title: 'Accounts Payable', type: PaymentType.Payable, icon: 'fa-credit-card', restrictions: [TenantStatus.Ready] }
                    // },
                    // {
                    //     path: 'reconcile-payments',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.payments.reconciliation').then(m => m.ReconciliationModule),
                    //     canLoad: [AuthorizeGuard],
                    //     data: { title: 'Reconcile Payments', icon: 'fa-balance-scale', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'payment-sync',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.payment.sync').then(m => m.PaymentSyncModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Payment Sync', icon: 'fa-cloud-download', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'sales-order',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.sales.order').then(m => m.SaleOrderModule),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('sales-orders')],
                        data: { title: 'Sales Order', icon: 'fa-money-check-dollar', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'purchase-orders',
                        loadChildren: () => import('@workbuddy/modules/boards/purchase.orders').then(m => m.PURCHASE_ORDERS_MODULE_ROUTES),
                        canActivate: [AuthorizeGuard, legacyEntityRouteGuard('purchase-orders')],
                        data: {
                            title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready]

                        }
                    },
                ]
            },
            {
                path: 'finance-pro',
                data: { title: 'Finance Pro', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready], topLevel: true },
                children: [
                    {
                        path: 'opportunities',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Opportunities',
                            icon: 'fa-handshake-o',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.Opportunity,
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'leads',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Leads',
                            icon: 'fa-users',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.Lead,
                                header: { title: 'Leads' },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'jobs',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Jobs',
                            icon: 'icomoon icomoon-jobs',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.Job,
                                restrictions: {
                                    recordScopes: [MetadataEntityRecordScope.Primary],
                                },
                                header: {
                                    title: 'Jobs',
                                    showSearch: true,
                                    showPaginator: true,
                                    showViewOptions: true,
                                    showDisplaySwitcher: true,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'stages',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Stages',
                            icon: 'fa-layer-group',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.Job,
                                restrictions: {
                                    recordScopes: [MetadataEntityRecordScope.Secondary],
                                },
                                header: {
                                    title: 'Stages',
                                    showSearch: true,
                                    showPaginator: true,
                                    showViewOptions: true,
                                    showDisplaySwitcher: true,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'items',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Items',
                            icon: 'fa-boxes-stacked',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                // JobItem roots the same jobs documents at its own metadata, so the items
                                // board inherits none of Job's column/filter/grouping affordances.
                                entity: ActivityEntityName.JobItem,
                                // Items are always parent-scoped (a job id or a stage id), and the Items
                                // view is registered as Embedded — embedded and system views are isolated
                                // from each other, so the mount must declare itself embedded to see it.
                                isEmbedded: true,
                                header: {
                                    title: 'Items',
                                    showSearch: true,
                                    // The strategy forces unpaged, so a pager would render inert.
                                    showPaginator: false,
                                    // Items ships a single system view: nothing to switch between,
                                    // favourite, author or edit.
                                    showViewOptions: false,
                                    // 12 codes across 4 workOrders (largest in local tarek DB)
                                    parentId: financeProProbeJobId,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'items-catalogue',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Items Catalogue',
                            icon: 'fa-barcode',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.Catalogue,
                                header: {
                                    title: 'Items Catalogue',
                                    showSearch: true,
                                    showPaginator: true,
                                    showViewOptions: true,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'tasks',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Tasks',
                            icon: 'fa-regular fa-list-check',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                // Parent-scoped like Items: Task strategy resolves via parentId → Job.
                                entity: ActivityEntityName.Task,
                                isEmbedded: true,
                                header: {
                                    title: 'Tasks',
                                    parentId: financeProProbeJobId,
                                    parentEntity: ActivityEntityName.Job,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'task-templates',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Task Templates',
                            icon: 'fa-regular fa-list-check',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                // Task templates are always parent-scoped (checklist / job template /
                                // stage / recurring job). Staging probe parent, same pattern as tasks.
                                entity: ActivityEntityName.TaskTemplate,
                                isEmbedded: true,
                                header: {
                                    title: 'Task Templates',
                                    parentId: financeProProbeChecklistId,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'files',
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Files',
                            icon: 'fa-regular fa-files',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                // Parent-scoped: File strategy resolves via parentId → Job (default parentEntity).
                                // Multi-select + Download selected come from fileManifest board defaults.
                                entity: ActivityEntityName.File,
                                isEmbedded: true,
                                header: {
                                    title: 'Files',
                                    // Same parent as finance-pro/tasks + items.
                                    parentId: financeProProbeJobId,
                                },
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'recurring-jobs',
                        // Surface Engine staging for Recurring Job. The live board stays at
                        // /jobs/recurring on its legacy dashboard until Schedules, Items and Tasks
                        // land on the Manage surface.
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Recurring Jobs',
                            icon: 'fa-repeat',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.RecurringJob,
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'job-templates',
                        // Surface Engine staging for Job Template. The live board stays at
                        // /jobs/job-templates on its legacy dashboard until the record page carries
                        // Stages, Items, Tasks and Questions.
                        loadComponent: () => import('@workbuddy/ui/v2/blocks/entity.board').then(m => m.EntityBoardComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Job Templates',
                            icon: 'fa-clone',
                            restrictions: [TenantStatus.Ready],
                            context: {
                                entity: ActivityEntityName.JobTemplate,
                            } satisfies EntityBoardContext,
                        },
                    },
                    {
                        path: 'general-settings',
                        loadComponent: () => import('@workbuddy/entities/settings.general/surface/settings/general.settings.route').then(m => m.GeneralSettingsRouteComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'General Settings',
                            icon: 'fa-cog',
                            restrictions: [TenantStatus.Ready],
                        },
                    },
                    {
                        // WBS-12288 redesign, side by side with general-settings until the team keeps one. System Administrators only:
                        // AuthorizeGuard admits them first and has no case for this path, so it refuses everyone else. Keep it that way
                        // until the team picks a design; there is deliberately no menu link.
                        path: 'general-settings-pro-max-plus',
                        loadComponent: () => import('@workbuddy/entities/settings.general.pro.max.plus/page/general.settings.pro.page').then(m => m.GeneralSettingsProPageComponent),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'General Settings',
                            icon: 'fa-cog',
                            restrictions: [TenantStatus.Ready],
                        },
                    },
                ]
            },
            // {
            //     path: 'purchase',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.purchase.orders').then(m => m.PurchaseOrdersModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready] }
            // },

            // {
            //     path: 'setup',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.tenant.setup/src/tenant.setup.module').then(m => m.TenantSetupModule),
            //     canActivate: [AuthorizeGuard],
            //     data: {
            //         title: 'Setup Wizard',
            //         icon: 'fa-magic',
            //         animation: 'homePage',
            //         restrictions: [TenantStatus.Confirmed, TenantStatus.Initializing, TenantStatus.Ready]
            //     }
            // },
            {
                path: 'reports',
                loadChildren: () => import('../../../../libs/tenant/module.jms.reporting').then(m => m.ReportingModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Reports', icon: 'fa-bar-chart', restrictions: [TenantStatus.Ready] }
            },

            {
                path: 'inbound-email',
                loadChildren: () => import('@workbuddy/modules/boards/inbound.email/inbound.email.routes').then(m => m.INBOUND_EMAIL_ROUTES),
                canActivate: [AuthorizeGuard],
                data: { title: 'Inbound Emails', icon: 'fa-envelope', restrictions: [TenantStatus.Ready], securityModule: SecurityModuleName.InboundEmail }
            },

            {
                path: 'inbox',
                loadChildren: () => import('@workbuddy/modules/boards/inbox').then(m => m.INBOX_ROUTES),
                // canMatch, not canActivate: a refused canActivate logs the user out, where a direct /inbox with
                // Command Center off (or without inbox rights) should simply not match.
                canMatch: [AuthorizeGuard],
                data: { title: 'Inbox', icon: 'fa-inbox', restrictions: [TenantStatus.Ready] }
            },

            {
                path: 'agent',
                loadComponent: () => import('@workbuddy/ai/agent').then(m => m.AgentFullpageChatComponent),
                canActivate: [AuthorizeGuard],
                canMatch: [agentRouteGuard],
                data: { title: 'Assistant', icon: 'fa-solid fa-robot', restrictions: [TenantStatus.Ready], securityModule: SecurityModuleName.AIAgent }
            },

            {
                path: 'settings',
                loadChildren: () => import('../../../../libs/tenant/module.jms.settings.shared').then(m => m.SettingsSharedModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Settings', icon: 'fa-cog', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'user/notifications',
                loadChildren: () => import('@workbuddy/settings/module.jms.settings.notifications').then(m => m.NOTIFICATIONS_ROUTES),
                canActivate: [AuthorizeGuard],
                data: { title: 'Notification Preferences', icon: 'fa-bell', restrictions: [TenantStatus.Ready], mode: 'user', hidden: true }
            },
            {
                path: 'user/security',
                loadChildren: () => import('@workbuddy/settings/module.jms.settings.security.user').then(m => m.USER_SECURITY_ROUTES),
                canActivate: [AuthorizeGuard],
                data: { title: 'Security & Permissions', icon: 'fa-shield-check', restrictions: [TenantStatus.Ready], mode: 'user', hidden: true }
            },
            // {
            //     path: 'formviewer',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.form.responses').then(m => m.FormResponseModule),
            //     data: { restrictions: [TenantStatus.Ready] }
            // },
            { path: '', redirectTo: 'home', pathMatch: 'full' },
        ]
    },
    { path: 'forms', loadChildren: () => import('../../../../libs/tenant/module.jms.form.responses').then(m => m.FormResponseModule) },

    { path: 'confirm/:id', loadComponent: () => import('@workbuddy/ui/blocks/invitation.confirm').then(m => m.InvitationConfirmComponent) },
    { path: 'callback', component: AuthoriseComponent },
    { path: 'portal-select', component: PortalSelectComponent },
    {
        path: 'consent',
        component: ConsentComponent,
        canActivate: [AuthGuard],
    },
    { path: 'inactive/user', component: InactiveUserComponent },
    { path: 'deleted/user', component: DeletedUserComponent },
    { path: 'inactive/status/user', component: InactiveStatusUserComponent },
    { path: 'inactive/tenant', component: InactiveTenantComponent },
    { path: 'login/access-denied', component: AccessDeniedComponent },
    { path: 'login/maintenance', component: MaintenanceTenantComponent },
    { path: 'ip-restricted', loadComponent: () => import('@workbuddy/ui/components/errors/ip.restriction.error/ip.restriction.error.component').then(m => m.IpRestrictionErrorComponent) },
    { path: 'trial-expired', loadComponent: () => import('@workbuddy/ui/components/errors/trial.expired.error/trial.expired.error.component').then(m => m.TrialExpiredErrorComponent) },
    {
        path: 'preview',
        children: [
            {
                path: 'quote',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.QuoteViewerModule)
            },
            {
                path: 'invoice',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.InvoiceViewerModule)
            },
            {
                path: 'bill',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.BillViewerModule)
            },
            {
                path: 'sale-order',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.SaleOrderViewerModule)
            },
            {
                path: 'po/:id',
                loadComponent: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.PurchaseOrderPreviewComponent)
            },
            {
                path: '',
                component: AccessDeniedComponent
            }
        ]
    },
    // {
    //     path: '_portals',
    //     children: [
    //         {
    //             path: 'clients',
    //             children: clientPortalRoute,
    //         },
    //     ]
    // },
    {
        path: 'public',
        children: [
            {
                path: 'quotes',
                loadChildren: () => import('../../../../libs/tenant/module.jms.public.quotes').then(m => m.PublicQuotesModule),
            },
            {
                path: 'invoices',
                loadChildren: () => import('../../../../libs/tenant/module.jms.public.invoices').then(m => m.PublicInvoicesModule),
            },
            {
                path: 'gantt-native/:jobId',
                loadComponent: () => import('@workbuddy/modules/boards/job/job.gantt.native/job.gantt.native.public.component').then(m => m.JobGanttNativePublicComponent),
                canMatch: [nativeJobGanttCanMatch],
                data: { title: 'Gantt — Native Preview', icon: 'fa-regular fa-chart-gantt', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'gantt/:jobId',
                loadComponent: () => import('@workbuddy/modules/boards/job/job.gantt.chart.public').then(m => m.JobGanttChartPublicComponent),
                data: { title: 'Gantt', icon: 'fa-regular fa-chart-gantt', restrictions: [TenantStatus.Ready] }
            },
            {
                path: '',
                redirectTo: 'quotes',
                pathMatch: 'full'
            }
        ]
    },
    // Wildcard route - must be last
    { path: '**', component: PageNotFoundComponent }

] as Routes;
